import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { Table } from '@cureskin/api-client';
import { ConnectionService } from '@services/connection-service';

export enum DoctorInfoBottomSheetSource {
  REGIMEN = 'regimen',
  REPORT = 'report',
}

export interface DoctorInfoBottomSheetData {
  doctor: Table.User;
  source: DoctorInfoBottomSheetSource.REGIMEN | DoctorInfoBottomSheetSource.REPORT;
  orderPlaced?: boolean;
  buyNowCallback?: () => void;
}

@Component({
  selector: 'app-doctor-info-bottomsheet',
  templateUrl: './doctor-info-bottomsheet.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DoctorInfoBottomSheetComponent {
  protected readonly data = inject<DoctorInfoBottomSheetData>(MAT_BOTTOM_SHEET_DATA);
  protected readonly conn = inject(ConnectionService);
  private readonly bottomSheetRef = inject(MatBottomSheetRef<DoctorInfoBottomSheetComponent>);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly scrollContainer = viewChild.required<ElementRef<HTMLElement>>('scrollContainer');
  protected readonly expandedHeader = viewChild.required<ElementRef<HTMLElement>>('expandedHeader');

  protected readonly isCompactHeaderVisible = signal(false);
  protected readonly showCTA = computed(
    () => this.data.source === DoctorInfoBottomSheetSource.REGIMEN && !this.data.orderPlaced,
  );

  private observer: IntersectionObserver | null = null;

  constructor() {
    afterNextRender(() => this.setupIntersectionObserver());
    this.destroyRef.onDestroy(() => this.observer?.disconnect());
  }

  protected closeSheet(): void {
    this.bottomSheetRef.dismiss();
  }

  protected onBuyNow(): void {
    this.data.buyNowCallback?.();
    this.bottomSheetRef.dismiss();
  }

  private setupIntersectionObserver(): void {
    this.observer = new IntersectionObserver(
      ([entry]) => this.isCompactHeaderVisible.set(!entry.isIntersecting),
      { root: this.scrollContainer().nativeElement, threshold: 0.4 },
    );
    this.observer.observe(this.expandedHeader().nativeElement);
  }
}
